import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signOut,
  onIdTokenChanged,
  type User,
} from "firebase/auth";

// Firebase web SDK config — apiKey is public, fine to embed at build time.
// Values come from VITE_FIREBASE_* env vars (set in deploy-uat.yml).
const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let _app: ReturnType<typeof initializeApp> | null = null;
let _auth: ReturnType<typeof getAuth> | null = null;

function ensure() {
  if (!_app) {
    if (!config.apiKey) {
      throw new Error(
        "Firebase web config missing. Set VITE_FIREBASE_API_KEY + friends in the build env.",
      );
    }
    _app = getApps()[0] ?? initializeApp(config);
    _auth = getAuth(_app);
  }
  return { app: _app!, auth: _auth! };
}

export function firebaseConfigured() {
  return Boolean(config.apiKey);
}

// Email-link sign-in flow.
//
// 1. requestSignInLink(email) — sends the magic link to the user's inbox.
// 2. User clicks link → app loads at /auth/finish with the magic URL.
// 3. completeSignInFromLink(email, href) — exchanges the link for a session.
//
// Email is held in localStorage between steps because the link can be opened
// in a different browser tab/device.

const LS_PENDING_EMAIL = "cura-studio:pending-signin-email";

export async function requestSignInLink(email: string) {
  const { auth } = ensure();
  const url = `${window.location.origin}/auth/finish`;
  await sendSignInLinkToEmail(auth, email, {
    url,
    handleCodeInApp: true,
  });
  window.localStorage.setItem(LS_PENDING_EMAIL, email);
}

export function isSignInLink(href: string) {
  if (!firebaseConfigured()) return false;
  const { auth } = ensure();
  return isSignInWithEmailLink(auth, href);
}

export async function completeSignInFromLink(href: string, email?: string) {
  const { auth } = ensure();
  const finalEmail = email ?? window.localStorage.getItem(LS_PENDING_EMAIL);
  if (!finalEmail) throw new Error("Email needed to complete sign-in. Re-enter it.");
  const result = await signInWithEmailLink(auth, finalEmail, href);
  window.localStorage.removeItem(LS_PENDING_EMAIL);
  return result.user;
}

export async function signOutCurrent() {
  if (!firebaseConfigured()) return;
  const { auth } = ensure();
  await signOut(auth);
}

export function onAuthChange(cb: (user: User | null, token: string | null) => void) {
  if (!firebaseConfigured()) {
    cb(null, null);
    return () => {};
  }
  const { auth } = ensure();
  return onIdTokenChanged(auth, async (u) => {
    if (!u) return cb(null, null);
    const token = await u.getIdToken();
    cb(u, token);
  });
}

export async function currentIdToken() {
  if (!firebaseConfigured()) return null;
  const { auth } = ensure();
  const u = auth.currentUser;
  if (!u) return null;
  return await u.getIdToken();
}

// waitForAuthReady resolves once Firebase has had a chance to hydrate the
// persisted session. Before that, `auth.currentUser` is null even for users
// with a valid session token in IndexedDB — leading to 401 storms on the
// first paint. Every API call awaits this so the Authorization header is
// always sent when a session exists. Times out after 3s as a safety net.
let _readyPromise: Promise<void> | null = null;
export function waitForAuthReady(): Promise<void> {
  if (!firebaseConfigured()) return Promise.resolve();
  if (_readyPromise) return _readyPromise;
  _readyPromise = new Promise((resolve) => {
    const { auth } = ensure();
    const unsub = onAuthStateChanged(auth, () => {
      unsub();
      resolve();
    });
    setTimeout(() => resolve(), 3000);
  });
  return _readyPromise;
}
